import React from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';

import {AppFlags} from '../../../app/app-types';
import {connectAppEnv} from '../../../app/app-env';
import {connectModuleEnv} from '../../oe-module-env';
import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import {OEIconButton} from '../../elements/controls/oe-button';
import OEDropdown from '../../elements/controls/oe-dropdown';
import {OEGroupControl, OEControl, OEIconButtonControl, OEIconButtonGroupControl, OELStringTextFieldControl} from '../../elements/controls/oe-controls';
import {OEObjectModelValueEditHelper} from '../../elements/object-model-value-edit/oe-object-model-value-edit-model';
import OEObjectModelValueEdit from '../../elements/object-model-value-edit/oe-object-model-value-edit';
import {oeUniqueIdGen} from '../../../lib/tools/oe-unique-id-gen';
import OEIconCodes from '../../../lib/oe-icon-codes';
import OEPopover from '../../views/oe-popover';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import retardUpdate from '../../../lib/react/update-retarder';
import {OEUserObjectMaterialPopoverController} from './oe-user-object-material-controller';
import {OEUserObjectChangeFlags, OEUserObjectGeometryType, OEUserObjectPropertyFlags, OEUserObjectDataFlags, OEUserObjectToolbox, OEUserObjectParameterModels, OEUserObjectPopoverBtnIds, OEUserObjectDataSlots} from './oe-user-object-model';

export class OEUserObjectPopoverController extends React.PureComponent {

    constructor(props)  {
        super(props);

        this.uid = oeUniqueIdGen.get();
        this.popoverTargetId = 'popover-target-id-' + this.uid;
        this.namePopoverTargetId = 'popover-target-id-name-' + this.uid;

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.geometryEntries = OEUserObjectToolbox.createGeometryTypeEntries();

        this.id = null;
        this.isOpen = false;
        this.position = null;

        this.state = this.defaultState();
        
        this.onWindowResized = this.onWindowResized.bind(this);

        this.updateLanguage = this.updateLanguage.bind(this);
        this.updateIsOpenState = this.updateIsOpenState.bind(this);
        this.onUserObjectLongTouched = this.onUserObjectLongTouched.bind(this);
        this.onUserObjectChanged = this.onUserObjectChanged.bind(this);
        this.onUserObjectRemoved = this.onUserObjectRemoved.bind(this);
        this.onUserObjectDataSlotChanged = this.onUserObjectDataSlotChanged.bind(this);
        this.onUIControllerStateChanged = this.onUIControllerStateChanged.bind(this);

        this.onTextureFileLoadInputRef = this.onTextureFileLoadInputRef.bind(this);

        this.renderAdditionalButtons = this.renderAdditionalButtons.bind(this);

        this.onToggle = this.onToggle.bind(this);
        this.onDumpBtnPressed = this.onDumpBtnPressed.bind(this);
        this.onFunctionButtonPressed = this.onFunctionButtonPressed.bind(this);
        this.onNameTextFieldChange = this.onNameTextFieldChange.bind(this);
        this.onTextureButtonPressed = this.onTextureButtonPressed.bind(this);
        this.onMaterialBtnPressed = this.onMaterialBtnPressed.bind(this);
        this.onGeometrySelected = this.onGeometrySelected.bind(this);
        this.onGeometryParameterChange = this.onGeometryParameterChange.bind(this);
        this.onTransformationChange = this.onTransformationChange.bind(this);
        this.onDataSlotButtonPressed = this.onDataSlotButtonPressed.bind(this);
        this.onTextureFileLoadInputResult = this.onTextureFileLoadInputResult.bind(this);
        this.onMaterialPopoverToggle = this.onMaterialPopoverToggle.bind(this);
    }

    defaultState()  {
        return {
            id: this.id,
            isOpen: this.isOpen,
            position: this.position,
            geometry: {
                type: OEUserObjectGeometryType.default,
                params: {}
            },
            geometryParameterModel: OEUserObjectParameterModels.modelForGeometryType(OEUserObjectGeometryType.default),
            hasTexture: false,
            transformation: {
                position: {x: 0, y: 0, z: 0},
                rotation: {x: 0, y: 0, z: 0}
            },
            properties: OEUserObjectPropertyFlags.default,
            name: new LString(),
            strings: {
                textureBtnGroupLabel: '',
                materialBtnLabel: '',
                geometryLabel: '',
                transformationLabel: '',
                tooltips: {
                    dump: '',
                    highlighting: '',
                    passthrough: '',
                    alignToCamera: '',
                    alignToCameraVPMainAxis: '',
                    cameraPivot: '',
                    loadTexture: '',
                    dumpTexture: '',
                    material: '',
                    copyGeometry: '',
                    pasteGeometry: '',
                    copyTransformation: '',
                    pasteTransformation: ''
                }
            },
            geometryEntries: clone(this.geometryEntries),
            isMaterialPopoverOpen: false,
            dataSlots: {
                geometry: false,
                transformation: false
            }
        };
    }
    
    componentDidMount()    {
        window.addEventListener('resize', this.onWindowResized);
    }

    componentWillUnmount()    {
        window.removeEventListener('resize', this.onWindowResized);
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.userObjectLongTouched, this.onUserObjectLongTouched);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.userObjectsEnabledChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.userObjectEditModeChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.userObjectChanged, this.onUserObjectChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.userObjectRemoved, this.onUserObjectRemoved);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.userObjectDataSlotChanged, this.onUserObjectDataSlotChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.userObjectLongTouched, this.onUserObjectLongTouched);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.userObjectsEnabledChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.userObjectEditModeChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.userObjectChanged, this.onUserObjectChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.userObjectRemoved, this.onUserObjectRemoved);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.userObjectDataSlotChanged, this.onUserObjectDataSlotChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);	
    }

    onOpenStateChanged()    {
        if(!this.oe.isReady()) return;
        let controller = this.oe.sharedInterface.getUIControllerModelState();
        controller.setUpdateEnabled(!this.isOpen);
        
        let userInfo = new this.oe.Module.Dictionary();
        this.oe.sharedInterface.postNotification(this.oe.NotificationName.uiInstanceUserObjectPopoverOpenStateChanged, userInfo.setBoolValue('open', this.isOpen));
        userInfo.delete();
    }

    close() {
        if(!this.isOpen) return;
        this.isOpen = false;
        this.setState({isOpen: false, isMaterialPopoverOpen: false});
        this.onOpenStateChanged();
    }

    open(pos)   {
        if(!this.canOpen()) return;

        if(this.isOpen)	{
            if(!pos || OEToolbox.shallowEqual(pos, this.position)) return;
            this.position = pos;
            this.setState({position: this.position});
            return;
        }

        this.isOpen = true;
        if(pos)	{
            this.position = pos;
            this.setState({isOpen: true, position: this.position});
        } else {
            this.setState({isOpen: true});
        }
        
        this.onOpenStateChanged();
    }
    
    canOpen()   {
        if(!this.oe.isReady()) return false;
        let controller = this.oe.sharedInterface.getUIControllerUserObject();
        return controller.getUIEnabled() && controller.getEnabled() && controller.isEditingEnabled();
    }

    getPosition()   {
        return this.position;
    }

    setPosition(pos)    {
        if(OEToolbox.shallowEqual(pos, this.position)) return;
        this.position = pos;
        this.setState({position: this.position});
    }
    
    updatePos() {
        if(!this.oe.isReady() || this.id === null) return;

        let pos = this.oe.sharedInterface.getUIControllerUserObject().getObjectScreenCenter(this.id);

        let devicePR = 1 / this.oe.Module.devicePixelRatio();
        pos = {x: pos.x * devicePR, y: pos.y * devicePR};

        this.setPosition(pos);
    }

    onWindowResized()   {
        this.updatePos();

        // the object is not correct repositioned, since the core module viewport has not been resized when the window sends the resize event
        // quick workaround -> update the position at a later time when the viewport has been updated 
        window.requestAnimationFrame(function() {
            window.requestAnimationFrame(this.updatePos.bind(this));
        }.bind(this));
    }

    updateLanguage()   {
        let si = this.oe.sharedInterface;

        this.geometryEntries.forEach(entry => {
            entry.label = si.getLocalizedStringEnc('user_object_view_geometry_' + entry.key);
        });

        this.setState({
            strings: {
                textureBtnGroupLabel: si.getLocalizedStringEnc('user_object_view_texture_btn_group'),
                materialBtnLabel: si.getLocalizedStringEnc('user_object_view_material_btn'),
                geometryLabel: si.getLocalizedStringEnc('user_object_view_geometry'),
                transformationLabel: si.getLocalizedStringEnc('user_object_view_transformation'),
                tooltips: {
                    dump: si.getLocalizedStringEnc('user_object_tooltip_dump'),
                    highlighting: si.getLocalizedStringEnc('user_object_tooltip_highlighting'),
                    passthrough: si.getLocalizedStringEnc('user_object_tooltip_passthrough'),
                    alignToCamera: si.getLocalizedStringEnc('user_object_tooltip_align_to_camera'),
                    alignToCameraVPMainAxis: si.getLocalizedStringEnc('user_object_tooltip_align_to_camera_vp_main_axis'),
                    cameraPivot: si.getLocalizedStringEnc('user_object_tooltip_camera_pivot'),
                    loadTexture: si.getLocalizedStringEnc('user_object_tooltip_load_texture'),
                    dumpTexture: si.getLocalizedStringEnc('user_object_tooltip_dump_texture'),
                    material: si.getLocalizedStringEnc('user_object_tooltip_material'),
                    copyGeometry: si.getLocalizedStringEnc('user_object_tooltip_copy_geometry'),
                    pasteGeometry: si.getLocalizedStringEnc('user_object_tooltip_paste_geometry'),
                    copyTransformation: si.getLocalizedStringEnc('user_object_tooltip_copy_transformation'),
                    pasteTransformation: si.getLocalizedStringEnc('user_object_tooltip_paste_transformation'),
                }
            },
            geometryEntries: clone(this.geometryEntries)
        });
    }

    updateUserObjectState()  {
        if(!this.oe.isReady() || this.id === null)    return;
        let controller = this.oe.sharedInterface.getUIControllerUserObject();

        let data = controller.getObjectData(this.id);
        let transform = controller.getObjectTransform(this.id);

        this.setState({
            geometry: {
                type: data.geometry.type.value,
                params: data.geometry.params
            },
            geometryParameterModel: OEUserObjectParameterModels.modelForGeometryType(data.geometry.type.value),
            hasTexture: controller.hasTexture(this.id),
            transformation: {
                position: transform.pos,
                rotation: transform.angles
            },
            properties: data.properties,
            name: data.name
        });
    }

    updateDataSlots()  {
        if(!this.oe.isReady())  return;
        let controller = this.oe.sharedInterface.getUIControllerUserObject();

        this.setState({dataSlots: {
            geometry: controller.hasObjectDataSlot(OEUserObjectDataSlots.geometry),
            transformation: controller.hasObjectDataSlot(OEUserObjectDataSlots.transformation)
        }});
    }

    updateIsOpenState(){
        if(!this.canOpen()) this.close();
    }

    updateState(released)   {
        if(!this.oe.isReady() || released)   {
            this.id = null;
            this.position = null;
            this.setState(this.defaultState());
            this.close();
            return;
        }

        retardUpdate(this, () => {
            this.updateIsOpenState();
            this.updateLanguage();
            this.updateUserObjectState();
            this.updateDataSlots();
        });
    }

    onUserObjectLongTouched(message, userInfo)   {
        if(!this.canOpen()) return;

        this.id = userInfo.ID;
        this.setState({id: this.id});

        let pos = {x: userInfo.screenPos.x, y: userInfo.screenPos.y};
        let devicePR = 1 / this.oe.Module.devicePixelRatio();
        pos = {x: pos.x * devicePR, y: pos.y * devicePR};

        this.open(pos);

        this.updateUserObjectState();
    }
    
    onUserObjectChanged(message, userInfo)   {
        if(userInfo.ID !== this.id || !(userInfo.changes & (OEUserObjectChangeFlags.geometry | OEUserObjectChangeFlags.transform | OEUserObjectChangeFlags.texture | OEUserObjectChangeFlags.properties | OEUserObjectChangeFlags.name)))  return;
        this.updateUserObjectState();
    }

    onUserObjectRemoved(message, userInfo) {
        if(userInfo.ID !== this.id)  return;
        this.close();
    }

    onUserObjectDataSlotChanged(message, userInfo)   {
        if(![OEUserObjectDataSlots.geometry, OEUserObjectDataSlots.transformation].includes(userInfo.slot)) return;
        this.updateDataSlots();
    }

    onUIControllerStateChanged(message, userInfo)    {
        if(userInfo.type === this.oe.Module.UIControllerType.user_object) {
            this.updateIsOpenState();
        }
    }

    onTextureFileLoadInputRef(ref)  {
        this.textureFileLoadInput = ref;
    }

    renderAdditionalButtons(props)  {
        return (
            <React.Fragment>
                <OEIconButton 
                    className={props.buttonClassName + ' dump-btn'}
                    icon={OEIconCodes.userObjectDump}
                    tooltip={this.state.strings.tooltips.dump}
                    onPressed={this.onDumpBtnPressed}
                />
            </React.Fragment>
        );
    }

    render()    {
        const position = !this.state.position ? null : {x: this.state.position.x + this.props.offset.x, y: this.state.position.y + this.props.offset.y};
        let geometryEntries = this.state.geometryEntries.map(entry => { return {key: entry.type, content: <span className="label">{entry.label}</span>} } );

        return (
            <React.Fragment>
                <input
                    type="file"
                    accept=".jpg,.png,.bmp"
                    ref={this.onTextureFileLoadInputRef}
                    style={{display: 'none'}}
                    onChange={this.onTextureFileLoadInputResult}
                />
                
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                
                <OEPopover
                    moduleId={this.props.moduleId}
                    className="popover-control"
                    boundariesElement={this.props.boundariesElement}
                    position={position}
                    buttonClassName="transparent-btn"
                    headerSeparator={false}
                    isOpen={this.state.isOpen && this.state.position !== null}
                    additionalButtons={this.renderAdditionalButtons}
                    backdrop={true}
                    backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                    onToggle={this.onToggle}
                >

                    <div className="user-object-popover compact">
                        <OEGroupControl popoverTargetId={this.popoverTargetId}>

                            <OEIconButtonGroupControl
                                buttons={[
                                    {
                                        id: OEUserObjectPopoverBtnIds.highlightingProperty,
                                        icon: OEIconCodes.userObjectHighlighting,
                                        activated: !(this.state.properties & OEUserObjectPropertyFlags.disableHighlighting),
                                        tooltip: this.state.strings.tooltips.highlighting
                                    },
                                    {
                                        id: OEUserObjectPopoverBtnIds.passthroughProperty,
                                        icon: OEIconCodes.userObjectPassthrough,
                                        activated: !!(this.state.properties & OEUserObjectPropertyFlags.passthrough),
                                        tooltip: this.state.strings.tooltips.passthrough
                                    },
                                    {
                                        id: OEUserObjectPopoverBtnIds.alignToCamera,
                                        icon: OEIconCodes.userObjectAlignToCamera,
                                        tooltip: this.state.strings.tooltips.alignToCamera
                                    },
                                    {
                                        id: OEUserObjectPopoverBtnIds.alignToCameraVPMainAxis,
                                        icon: OEIconCodes.userObjectAlignToCameraVPMainAxis,
                                        tooltip: this.state.strings.tooltips.alignToCameraVPMainAxis
                                    },
                                    {
                                        id: OEUserObjectPopoverBtnIds.cameraPivot,
                                        icon: OEIconCodes.userObjectCameraPivot,
                                        activated: !!(this.state.properties & OEUserObjectPropertyFlags.cameraPivot),
                                        tooltip: this.state.strings.tooltips.cameraPivot
                                    }
                                ]}
                                onPressed={this.onFunctionButtonPressed}
                            />

                            <OELStringTextFieldControl
                                moduleId={this.props.moduleId}
                                popoverClassName="with-backdrop"
                                boundariesElement={this.props.boundariesElement}
                                popoverTargetId={this.namePopoverTargetId}
                                popoverTarget={this.namePopoverTargetId}
                                popoverPlacement={'right-start'}
                                hidePopoverArrow={true}
                                value={this.state.name}
                                onChange={this.onNameTextFieldChange}
                            />

                            <OEIconButtonGroupControl
                                title={this.state.strings.textureBtnGroupLabel}
                                buttons={[
                                    {
                                        id: OEUserObjectPopoverBtnIds.loadTexture,
                                        icon: OEIconCodes.userObjectTextureLoad,
                                        tooltip: this.state.strings.tooltips.loadTexture
                                    },
                                    {
                                        id: OEUserObjectPopoverBtnIds.dumpTexture,
                                        disabled: !this.state.hasTexture,
                                        icon: OEIconCodes.userObjectTextureDump,
                                        tooltip: this.state.strings.tooltips.dumpTexture
                                    }
                                ]}
                                onPressed={this.onTextureButtonPressed}
                            />

                            <OEIconButtonControl
                                className="align-right"
                                title={this.state.strings.materialBtnLabel}
                                activated={this.state.isMaterialPopoverOpen}
                                icon={OEIconCodes.userObjectMaterial}
                                tooltip={this.state.strings.tooltips.material}
                                onPressed={this.onMaterialBtnPressed}
                            />

                        </OEGroupControl>

                        <OEGroupControl title={this.state.strings.geometryLabel}>

                            {!this.props.devFeatures ? null :
                                <OEIconButtonGroupControl
                                    buttons={[
                                        {
                                            id: OEUserObjectPopoverBtnIds.copyGeometry,
                                            icon: OEIconCodes.userObjectCopyGeometry,
                                            tooltip: this.state.strings.tooltips.copyGeometry
                                        },
                                        {
                                            id: OEUserObjectPopoverBtnIds.pasteGeometry,
                                            disabled: !this.state.dataSlots.geometry,
                                            icon: OEIconCodes.userObjectPasteGeometry,
                                            tooltip: this.state.strings.tooltips.pasteGeometry
                                        }
                                    ]}
                                    onPressed={this.onDataSlotButtonPressed}
                                />
                            }

                            <OEControl>
                                <OEDropdown
                                    title={this.state.geometryEntries[this.state.geometry.type].label}
                                    chosenKey={this.state.geometry.type}
                                    entries={geometryEntries}
                                    onClick={this.onGeometrySelected}
                                />
                            </OEControl>

                            <OEControl>
                                <OEObjectModelValueEdit
                                    className="multi-column"
                                    model={this.state.geometryParameterModel}
                                    object={this.state.geometry.params}
                                    onChange={this.onGeometryParameterChange}
                                />
                            </OEControl>

                        </OEGroupControl>

                        <OEGroupControl title={this.state.strings.transformationLabel}>

                            {!this.props.devFeatures ? null :
                                <OEIconButtonGroupControl
                                    buttons={[
                                        {
                                            id: OEUserObjectPopoverBtnIds.copyTransformation,
                                            icon: OEIconCodes.userObjectCopyTransformation,
                                            tooltip: this.state.strings.tooltips.copyTransformation
                                        },
                                        {
                                            id: OEUserObjectPopoverBtnIds.pasteTransformation,
                                            disabled: !this.state.dataSlots.transformation,
                                            icon: OEIconCodes.userObjectPasteTransformation,
                                            tooltip: this.state.strings.tooltips.pasteTransformation
                                        }
                                    ]}
                                    onPressed={this.onDataSlotButtonPressed}
                                />
                            }

                            <OEControl>
                                <OEObjectModelValueEdit
                                    model={OEUserObjectParameterModels.transformation}
                                    object={this.state.transformation}
                                    onChange={this.onTransformationChange}
                                />
                            </OEControl>

                        </OEGroupControl>

                    </div>

                    <OEUserObjectMaterialPopoverController
                        moduleId={this.props.moduleId}
                        className="with-backdrop"
                        id={this.state.id}
                        boundariesElement={this.props.boundariesElement}
                        target={this.popoverTargetId}
                        placement="left-start"
                        hideArrow={true}
                        noHeader={true}
                        isOpen={this.state.isMaterialPopoverOpen}
                        onToggle={this.onMaterialPopoverToggle}
                        backdrop={false}
                        backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                        devFeatures={this.props.devFeatures}
                    />

                </OEPopover>
            </React.Fragment>
        );
    }
    
    onToggle() {
        this.close();
    }

    onDumpBtnPressed()  {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerUserObject().removeObject(this.id, true, 0.333);
    }

    onFunctionButtonPressed(sender, id)   {
        if(!this.oe.isReady() || this.id === null) return;
        let controller = this.oe.sharedInterface.getUIControllerUserObject();

        if(id === OEUserObjectPopoverBtnIds.highlightingProperty)   {
            controller.toggleObjectProperties(this.id, OEUserObjectPropertyFlags.disableHighlighting);
        } else if(id === OEUserObjectPopoverBtnIds.passthroughProperty) {
            controller.toggleObjectProperties(this.id, OEUserObjectPropertyFlags.passthrough);
        } else if(id === OEUserObjectPopoverBtnIds.cameraPivot) {
            controller.toggleObjectProperties(this.id, OEUserObjectPropertyFlags.cameraPivot);
        } else if(id === OEUserObjectPopoverBtnIds.alignToCamera)    {
            controller.alignToCamera(this.id);
        } else if(id === OEUserObjectPopoverBtnIds.alignToCameraVPMainAxis)    {
            controller.alignToCameraVPMainAxis(this.id);
        }
    }

    onNameTextFieldChange(sender, value)    {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerUserObject().setObjectName(this.id, value, undefined);
    }

    onTextureButtonPressed(sender, id)  {
        if(!this.oe.isReady() || this.id === null) return;

        if(id === OEUserObjectPopoverBtnIds.loadTexture)    {
            if(!this.textureFileLoadInput) return;
            this.textureFileLoadInput.value = null; // empties the file list so that onTextureFileLoadInputResult gets called when loading the same file multiple times successively
            this.textureFileLoadInput.click();
        } else if(id === OEUserObjectPopoverBtnIds.dumpTexture) {
            this.oe.sharedInterface.getUIControllerUserObject().setTexture(this.id, undefined);
        }
    }

    onMaterialBtnPressed()  {
        this.setState({isMaterialPopoverOpen: !this.state.isMaterialPopoverOpen});
    }

    onGeometrySelected(type)    {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerUserObject().setObjectGeometryType(this.id, {value: type});
    }

    onGeometryParameterChange(sender, params_) {
        if(!this.oe.isReady() || this.id === null) return;

        let params = clone(params_);
        if(params)  params.$__model__ = OEObjectModelValueEditHelper.modelToCoreDictModel(clone(sender.props.model));

        this.oe.sharedInterface.getUIControllerUserObject().setObjectGeometryParams(this.id, params);
    }

    onTransformationChange(sender, params) {
        if(!this.oe.isReady() || this.id === null) return;

        let transform = {
            pos: params.position,
            angles: params.rotation,
        };

        this.oe.sharedInterface.getUIControllerUserObject().setObjectTransform(this.id, transform);
    }

    onDataSlotButtonPressed(sender, id)   {
        if(!this.oe.isReady() || this.id === null) return;
        let controller = this.oe.sharedInterface.getUIControllerUserObject();

        if(id === OEUserObjectPopoverBtnIds.copyGeometry)    {
            controller.fillObjectDataSlot(OEUserObjectDataSlots.geometry, this.id);
        } else if(id === OEUserObjectPopoverBtnIds.pasteGeometry) {
            controller.applyObjectDataSlot(OEUserObjectDataSlots.geometry, this.id, OEUserObjectDataFlags.geometry);
        } else if(id === OEUserObjectPopoverBtnIds.copyTransformation)    {
            controller.fillObjectDataSlot(OEUserObjectDataSlots.transformation, this.id);
        } else if(id === OEUserObjectPopoverBtnIds.pasteTransformation) {
            controller.applyObjectDataSlot(OEUserObjectDataSlots.transformation, this.id, OEUserObjectDataFlags.transform);
        }
    }

    onTextureFileLoadInputResult(event)  {
        event.stopPropagation();
        event.preventDefault();

        if(!this.oe.isReady() || this.id === null) return;
        if(event.target.files.length == 0)  return;

        this.props.appComponent.showWaitingController();

        let files = [event.target.files[0]];

        let allFileProgress = {i: 0, num: files.length};

        let onLoadFunc = function(event)  {
            if(event.target.readyState != FileReader.DONE)  return;

            if(this.oe.isReady() && this.id !== null)    {
                let arrayBuffer = event.target.result;
                this.oe.sharedInterface.getUIControllerUserObject().setTextureData(this.id, arrayBuffer);
            }

            allFileProgress.i = allFileProgress.i + 1;
            if(allFileProgress.i == allFileProgress.num)    this.props.appComponent.hideWaitingController();

         }.bind(this);

        for(let i = 0; i < files.length; ++i)  {
            let reader = new FileReader();
            reader.onload = onLoadFunc;
            reader.readAsArrayBuffer(files[i]);
        }
    }

    onMaterialPopoverToggle()   {
        this.setState({isMaterialPopoverOpen: false});
    }
}

OEUserObjectPopoverController.defaultProps = {
    moduleId: '',
    devFeatures: false
};

OEUserObjectPopoverController.propTypes = {
    moduleId: PropTypes.string,
    devFeatures: PropTypes.bool
}; 

export default frefFromRef(connectAppEnv((env) => { 
    return {
        appComponent: env.component,
        devFeatures: env.config.module.flags && env.config.module.flags.includes(AppFlags.import)
    };
})(connectModuleEnv((env) => {
    return {
        offset: {x: 0, y: -env.ui.capBar.edgeOffsets.top}
    };
})(frefToRef(OEUserObjectPopoverController))));